import { Button, ButtonColors } from '@chiroup/components/Button';
import {
  AdminTestEmailRecipient,
  AdminTestPatientEnsureRequest,
} from '@chiroup/core/types/AdminTestPatient.type';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import useAdminMe from '../../../hooks/useAdminMe';
import testPatientService from '../../../services/testPatient.service';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'The request failed. Please try again.';

type Props = {
  clinicId: number;
  clinicName: string;
  users: AdminTestEmailRecipient[];
};

const ClinicTestPatient: React.FC<Props> = ({ clinicId, clinicName, users }) => {
  const queryClient = useQueryClient();
  const adminMe = useAdminMe();
  const [search, setSearch] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [selected, setSelected] = useState<number[]>([]);
  const [emailReportId, setEmailReportId] = useState('');
  const [recipientUserId, setRecipientUserId] = useState('');
  const [emailTitle, setEmailTitle] = useState(`Your Treatment Plan from ${clinicName}`);
  const [emailBody, setEmailBody] = useState(
    `Here is a sample condition report generated for ${clinicName}.`
  );
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const queryKey = ['clinicTestPatient', clinicId];
  const status = useQuery(queryKey, () => testPatientService.status(clinicId), {
    refetchOnWindowFocus: false,
  });
  const conditions = useQuery(
    ['clinicTestConditions', clinicId],
    () => testPatientService.conditions(clinicId),
    { refetchOnWindowFocus: false }
  );
  const ensure = useMutation(
    (body: AdminTestPatientEnsureRequest) => testPatientService.ensure(clinicId, body),
    {
      onSuccess: data => {
        queryClient.setQueryData(queryKey, data);
      },
    }
  );
  const createReport = useMutation(
    () => testPatientService.createReport(clinicId, { conditionIds: selected, requestId }),
    {
      onSuccess: data => {
        setRequestId(crypto.randomUUID());
        setSelected([]);
        setSearch('');
        setEmailReportId(String(data.ID));
        status.refetch();
      },
      onError: () => {
        status.refetch();
      },
    }
  );
  const emailReport = useMutation(() =>
    testPatientService.emailReport(clinicId, Number(emailReportId), {
      recipientUserId,
      title: emailTitle,
      body: emailBody,
      fromName,
      fromEmail,
    })
  );
  const patient = status.data?.patient;
  const busy = ensure.isLoading || createReport.isLoading;
  const emailRecipients = useMemo(
    () =>
      users
        .filter(
          user =>
            user.email?.trim() &&
            !['removed', 'deleted'].includes(user.membershipStatus?.toLowerCase() || '')
        )
        .sort((a, b) =>
          `${a.fname} ${a.lname}`.localeCompare(`${b.fname} ${b.lname}`, undefined, {
            sensitivity: 'base',
          })
        ),
    [users]
  );
  useEffect(() => {
    if (!emailReportId && status.data?.reports[0]) {
      setEmailReportId(String(status.data.reports[0].ID));
    }
  }, [emailReportId, status.data?.reports]);
  useEffect(() => {
    if (!adminMe.data) return;
    setFromName(
      current =>
        current ||
        [adminMe.data.firstName, adminMe.data.lastName].filter(Boolean).join(' ') ||
        'ChiroUp'
    );
    setFromEmail(current => current || adminMe.data.email);
  }, [adminMe.data]);
  const filtered = (conditions.data || []).filter(condition =>
    condition.name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Test patient</h2>
          <Button
            text="Refresh"
            color={ButtonColors.plainWithBorder}
            disabled={busy || status.isFetching}
            onClick={() => status.refetch()}
          />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage this clinic’s sample patient and create condition reports with the standard
          condition defaults. Creating a report does not email anyone automatically.
        </p>
        {status.isLoading && (
          <p className="mt-4" role="status">
            Loading test patient…
          </p>
        )}
        {status.isError && (
          <p className="mt-4 text-red-600 dark:text-red-400" role="alert">
            Unable to load the test patient. {errorMessage(status.error)}
          </p>
        )}
        {status.data && (
          <div className="mt-4 space-y-3">
            <p>
              Status:{' '}
              <strong>
                {patient
                  ? patient.disabled
                    ? 'Deleted (inactive)'
                    : 'Active'
                  : status.data.legacyCandidates.length
                    ? 'Needs confirmation'
                    : 'Not found'}
              </strong>
            </p>
            {patient && (
              <>
                <p className="break-all text-sm">
                  Patient ID: <code>{patient.ID}</code>
                </p>
                <a
                  className="block break-all text-sm text-primary-700 underline dark:text-primary-400"
                  href={patient.patientUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {patient.patientUrl}
                </a>
                <p className="text-xs text-muted-foreground">
                  Patient and report links require ChiroUp access with this clinic selected.
                </p>
                {patient.disabled && (
                  <Button
                    text="Reactivate test patient"
                    loading={ensure.isLoading}
                    disabled={busy}
                    onClick={() => ensure.mutate({})}
                  />
                )}
              </>
            )}
            {!patient && !status.data.legacyCandidates.length && (
              <Button
                text="Create test patient"
                loading={ensure.isLoading}
                disabled={busy}
                onClick={() => ensure.mutate({})}
              />
            )}
            {!patient && status.data.legacyCandidates.length > 0 && (
              <>
                <p className="text-sm">
                  Existing patients are named “Sample Patient” but have no test-patient marker.
                  Confirm which record is a demo patient before using it. Confirmation also
                  reactivates that patient.
                </p>
                {status.data.legacyCandidates.map(candidate => (
                  <div
                    key={candidate.ID}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm">
                        {candidate.disabled ? 'Deleted (inactive)' : 'Active'}
                      </p>
                      <a
                        className="break-all text-sm text-primary-700 underline dark:text-primary-400"
                        href={candidate.patientUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {candidate.ID}
                      </a>
                    </div>
                    <Button
                      text="Confirm as test patient"
                      disabled={busy}
                      loading={ensure.isLoading}
                      onClick={() => ensure.mutate({ legacyPatientId: candidate.ID })}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        {ensure.isError && (
          <p className="mt-3 text-red-600 dark:text-red-400" role="alert">
            {errorMessage(ensure.error)}
          </p>
        )}
      </section>
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h2 className="text-lg font-semibold">Create a condition report</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Choose up to 20 conditions for one combined report. Common treatments, advice, and
          exercises are added automatically. You can create additional reports afterward.
        </p>
        {!patient || patient.disabled ? (
          <p className="mt-4 text-sm">Activate a test patient to create reports.</p>
        ) : (
          <>
            <label className="mt-4 block text-sm font-medium" htmlFor="test-condition-search">
              Find conditions
            </label>
            <input
              id="test-condition-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search by condition name"
              className="mt-1 w-full rounded-md border-input bg-background text-foreground"
              disabled={busy}
            />
            {conditions.isLoading && <p role="status">Loading conditions…</p>}
            {conditions.isError && (
              <div role="alert">
                <p>Unable to load conditions.</p>
                <Button text="Retry conditions" onClick={() => conditions.refetch()} />
              </div>
            )}
            <div
              className="mt-3 max-h-72 overflow-y-auto rounded-md border border-border p-2"
              aria-label="Conditions"
            >
              {filtered.map(condition => (
                <label
                  key={condition.ID}
                  className="flex cursor-pointer items-center gap-3 rounded p-2 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(condition.ID)}
                    disabled={busy || (!selected.includes(condition.ID) && selected.length >= 20)}
                    onChange={event => {
                      setRequestId(crypto.randomUUID());
                      setSelected(previous =>
                        event.target.checked
                          ? [...previous, condition.ID]
                          : previous.filter(id => id !== condition.ID)
                      );
                    }}
                  />
                  <span className="text-sm">{condition.name}</span>
                </label>
              ))}
              {conditions.isSuccess && !filtered.length && (
                <p className="p-2 text-sm text-muted-foreground">No matching conditions.</p>
              )}
            </div>
            <p className="my-3 text-sm" aria-live="polite">
              {selected.length} selected
              {selected.length
                ? `: ${(conditions.data || [])
                    .filter(condition => selected.includes(condition.ID))
                    .map(condition => condition.name)
                    .join(', ')}`
                : ''}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                text="Create report"
                loading={createReport.isLoading}
                disabled={busy || !selected.length || conditions.isError}
                onClick={() => createReport.mutate()}
              />
              <Button
                text="Clear selection"
                color={ButtonColors.plainWithBorder}
                disabled={busy || !selected.length}
                onClick={() => {
                  setRequestId(crypto.randomUUID());
                  setSelected([]);
                }}
              />
            </div>
          </>
        )}
        {createReport.isError && (
          <p className="mt-3 text-red-600 dark:text-red-400" role="alert">
            {errorMessage(createReport.error)} Check the report list before retrying.
          </p>
        )}
        {createReport.isSuccess && (
          <p className="mt-3 text-green-700 dark:text-green-400" role="status">
            Report {createReport.data.ID} created.{' '}
            <Link
              className="underline"
              to={`/clinics/${clinicId}/test-patient/reports/${createReport.data.ID}`}
            >
              View and print
            </Link>{' '}
            or{' '}
            <a
              className="underline"
              href={createReport.data.reportUrl}
              target="_blank"
              rel="noreferrer"
            >
              open it in ChiroUp
            </a>
            .
          </p>
        )}
      </section>
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h2 className="text-lg font-semibold">Email a condition report</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Send the same condition report email produced by ChiroUp to a user at this clinic. Your
          message replaces the patient greeting while the report content and clinic branding stay
          intact.
        </p>
        {!status.data?.reports.length ? (
          <p className="mt-4 text-sm">Create a report before sending an email.</p>
        ) : !emailRecipients.length ? (
          <p className="mt-4 text-sm">This clinic has no active users with an email address.</p>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label
              className="flex flex-col gap-1 text-sm font-medium"
              htmlFor="test-report-email-report"
            >
              Report
              <select
                id="test-report-email-report"
                name="reportId"
                value={emailReportId}
                onChange={event => setEmailReportId(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              >
                {status.data.reports.map(report => (
                  <option key={report.ID} value={report.ID}>
                    Report {report.ID}: {report.conditions || 'Condition report'}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="flex flex-col gap-1 text-sm font-medium"
              htmlFor="test-report-email-recipient"
            >
              Recipient
              <select
                id="test-report-email-recipient"
                name="recipientUserId"
                value={recipientUserId}
                onChange={event => setRecipientUserId(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              >
                <option value="">Select a clinic user</option>
                {emailRecipients.map(user => (
                  <option key={user.ID} value={user.ID}>
                    {`${[user.fname, user.lname].filter(Boolean).join(' ') || user.email} (${user.email})`}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="flex flex-col gap-1 text-sm font-medium sm:col-span-2"
              htmlFor="test-report-email-title"
            >
              Email title
              <input
                id="test-report-email-title"
                name="title"
                type="text"
                value={emailTitle}
                maxLength={200}
                onChange={event => setEmailTitle(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              />
            </label>
            <label
              className="flex flex-col gap-1 text-sm font-medium sm:col-span-2"
              htmlFor="test-report-email-body"
            >
              Message body
              <textarea
                id="test-report-email-body"
                name="body"
                value={emailBody}
                rows={5}
                maxLength={4000}
                onChange={event => setEmailBody(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              />
            </label>
            <label
              className="flex flex-col gap-1 text-sm font-medium"
              htmlFor="test-report-email-from-name"
            >
              From name
              <input
                id="test-report-email-from-name"
                name="fromName"
                type="text"
                value={fromName}
                maxLength={100}
                onChange={event => setFromName(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              />
            </label>
            <label
              className="flex flex-col gap-1 text-sm font-medium"
              htmlFor="test-report-email-from-email"
            >
              From email (Reply-To)
              <input
                id="test-report-email-from-email"
                name="fromEmail"
                type="email"
                value={fromEmail}
                maxLength={254}
                onChange={event => setFromEmail(event.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-base text-foreground sm:text-sm"
                disabled={emailReport.isLoading}
              />
            </label>
            <p className="text-sm text-muted-foreground sm:col-span-2">
              The verified sending address remains no-reply@healthcom.io. The From name appears in
              the recipient’s inbox, and replies go to the From email above.
            </p>
            <div className="sm:col-span-2">
              <Button
                text="Send report email"
                loading={emailReport.isLoading}
                disabled={
                  emailReport.isLoading ||
                  !emailReportId ||
                  !recipientUserId ||
                  !emailTitle.trim() ||
                  !emailBody.trim() ||
                  !fromName.trim() ||
                  !fromEmail.trim()
                }
                onClick={() => emailReport.mutate()}
              />
            </div>
          </div>
        )}
        {emailReport.isError && (
          <p className="mt-3 text-red-600 dark:text-red-400" role="alert">
            {errorMessage(emailReport.error)}
          </p>
        )}
        {emailReport.isSuccess && (
          <p className="mt-3 text-green-700 dark:text-green-400" role="status">
            Condition report email sent to {emailReport.data.sentTo}.
          </p>
        )}
      </section>
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h2 className="text-lg font-semibold">Recent reports</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Latest 50 reports for this test patient.
        </p>
        {!status.data?.reports.length && (
          <p className="mt-4 text-sm text-muted-foreground">No reports to display.</p>
        )}
        <ul className="mt-3 divide-y divide-border">
          {status.data?.reports.map(report => (
            <li
              key={report.ID}
              className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{report.conditions || 'Condition report'}</p>
                <p className="text-xs text-muted-foreground">
                  Report {report.ID} · {String(report.createdAt).slice(0, 10)}
                </p>
                <a
                  className="mt-1 block break-all text-sm text-primary-700 underline dark:text-primary-400"
                  href={report.reportUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {report.reportUrl}
                </a>
              </div>
              <Link to={`/clinics/${clinicId}/test-patient/reports/${report.ID}`}>
                <Button text="View and print" color={ButtonColors.plainWithBorder} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
export default ClinicTestPatient;
